import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_GET, DBRequest, useConfig } from '../../../api/api';
import { numberToString } from '../../special/numberConverter';

// Lets the user pick a travel expense preset for the currently selected project
// and fills the related fields (distance, price per km, duration) from it.
// Requires a sibling `project` field (a project object with an id).
const TypeTravelExpensePreset = ({
  dataCollection,
  field,
  updateValidationOnChange,
  size,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const projectId = dataCollection?.[field.projectKey || 'project']?.id;
  const currencySymbol = config?.currency?.currencySymbol ?? '';
  const targetKey = field.targetKey || 'travelExpensePresetId';

  const [presets, setPresets] = useState([]);
  // Initialize from a pre-filled selection (e.g. when opened from a preset).
  const [value, setValue] = useState(dataCollection?.[targetKey] || '');
  const prevProjectIdRef = useRef(projectId);

  useEffect(() => {
    // Only reset the selection when the project actually changes — not on the
    // first mount, so a pre-filled preset selection is preserved.
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      setValue('');
      updateValidationOnChange(targetKey, null);
    }
    setPresets([]);
    if (!projectId || !config) return;
    DBRequest({
      config,
      path: `travel-expense-presets?projectId[eq]=${projectId}`,
      method: API_GET,
      onResponse: (res) => setPresets(res.data || []),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, config]);

  function presetLabel(preset) {
    const parts = [];
    const route = [preset.fromLocation, preset.toLocation]
      .filter(Boolean)
      .join(' \u2192 ');
    const address = preset.destinationAddress;
    const location =
      route ||
      (address &&
        ([address.zipCode, address.city].filter(Boolean).join(' ') ||
          address.street));
    if (location) parts.push(location);
    parts.push(`${numberToString(preset.distanceKilometers, 2)} km`);
    parts.push(`${numberToString(preset.totalPrice, 2)} ${currencySymbol}`);
    return parts.join(' \u00b7 ');
  }

  function handleChange(e) {
    const id = e.target.value;
    setValue(id);

    const preset = presets.find((item) => item.id === id);
    if (!preset) {
      updateValidationOnChange(targetKey, null);
      return;
    }

    updateValidationOnChange(
      null,
      {
        [targetKey]: preset.id,
        distanceKilometers: preset.distanceKilometers,
        pricePerKilometer: preset.pricePerKilometer,
        durationHours: preset.durationHours,
        destinationAddress: preset.destinationAddress ?? null,
      },
      true
    );
  }

  return (
    <FormControl fullWidth size={size} disabled={!projectId}>
      <InputLabel id="travel-expense-preset-label">
        {projectId ? field.label : t('Select a project first')}
      </InputLabel>
      <Select
        labelId="travel-expense-preset-label"
        value={value}
        label={projectId ? field.label : t('Select a project first')}
        onChange={handleChange}
      >
        <MenuItem value="">{t('No Select')}</MenuItem>
        {presets.map((preset) => (
          <MenuItem key={preset.id} value={preset.id}>
            {presetLabel(preset)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default TypeTravelExpensePreset;
